import classNames from "classnames";
import React, { useEffect, useState } from "react";
import { IDrawableSvg, IDrawableSvgShape, loadDrawableSvg } from "../../../shared/utility/drawable-svg";

export interface KuikaDrawnSvgProps {
  imageSrc: string;
  className: string;
  style: React.CSSProperties;
  /** Rendered instead when the icon cannot be drawn (no network, not an SVG, nothing to trace). */
  fallback: React.ReactElement;
}

type DrawnSvgState = IDrawableSvg | "loading" | "failed";

/**
 * KuikaSvg's `draw` mode: the icon's own strokes trace themselves on (`kuika-svg.scss`). Until the
 * file has loaded the box stays empty — showing the masked icon first would pop the glyph in and then
 * redraw it.
 */
const KuikaDrawnSvg: React.FC<KuikaDrawnSvgProps> = ({ imageSrc, className, style, fallback }) => {
  const [drawable, setDrawable] = useState<DrawnSvgState>("loading");

  useEffect((): (() => void) => {
    let isMounted: boolean = true;
    setDrawable("loading");

    loadDrawableSvg(imageSrc).then((loaded: IDrawableSvg | null): void => {
      if (isMounted) {
        setDrawable(loaded ?? "failed");
      }
    });

    return (): void => {
      isMounted = false;
    };
  }, [imageSrc]);

  if (drawable === "failed") {
    return fallback;
  }

  return (
    <div className={classNames(className, "kuika-svg--drawn")} style={style}>
      {drawable !== "loading" && (
        <svg viewBox={drawable.viewBox} aria-hidden="true" focusable="false">
          {drawable.shapes.map((shape: IDrawableSvgShape, index: number): React.ReactElement =>
            React.createElement(shape.tag, {
              ...shape.props,
              key: index,
              className: `kuika-svg__${shape.kind}`
            })
          )}
        </svg>
      )}
    </div>
  );
};

export default KuikaDrawnSvg;
